import * as React from 'react';
import {IListPayload} from 'models/generic';

const DEFAULT_LIMIT = 50;

export interface ISearchData<T, U> {
  data: T[] | IListPayload<T>;
  currentData: T[] | IListPayload<T>;
  isFetching: boolean;
  isError: boolean;
  error: Error;
  isLoading: boolean;
  search: (params: U) => void;
}

interface IUseSearchLazyLoadConfigContext<T, U> {
  items: T[];
  totalCount?: number;
  isLazyLoading: boolean;
  lazyLoad: () => void;
  isError: boolean;
  error: Error;
  isLoading: boolean;
  search: (params: U) => void;
  reset: (resetForm?: boolean) => void;
  searchParams: U;
  clearItems: () => void;
}

// T type returned by the endpoint
// U params for the endpoint
// V useProvider hook
export const getSearchLazyLoadProviderContext = <T, U extends Record<string, unknown>, V extends ISearchData<T, U>>(): {
  SearchContext: React.Context<IUseSearchLazyLoadConfigContext<T, U> | null>;
  SearchLazyLoadProvider: React.FC<{useProvider: () => V}>;
  useSearchLazyLoadProvider: () => IUseSearchLazyLoadConfigContext<T, U>;
} => {
  const SearchContext = React.createContext<IUseSearchLazyLoadConfigContext<T, U> | null>(null);

  return {
    SearchContext,
    SearchLazyLoadProvider: ({children, useProvider}) => {
      const limitRef = React.useRef<number>(DEFAULT_LIMIT);
      const offsetRef = React.useRef<number>(0);
      const paramsRef = React.useRef<U>({} as U);
      const hasMoreRef = React.useRef<boolean>(false);

      const [itemsData, setItemsData] = React.useState<{items: T[]; totalCount?: number}>({
        items: [],
        totalCount: 0,
      });

      const {items, totalCount} = itemsData;
      const {isFetching, error, isError, data, search: doSearch} = useProvider();

      React.useEffect(() => {
        if (error && 'status' in error && error.status === 404) {
          hasMoreRef.current = false;
        }
      }, [error]);

      const search = React.useCallback(
        (params?: U) => {
          const limit = (params?.limit as number) || DEFAULT_LIMIT;
          limitRef.current = limit;
          hasMoreRef.current = false;
          paramsRef.current = params ?? ({} as U);
          offsetRef.current = 0;

          setItemsData(() => ({items: [], totalCount: 0}));
          doSearch({...(params as U), offset: offsetRef.current, limit});
        },
        [doSearch],
      );

      const clearItems = React.useCallback(() => {
        setItemsData({items: [], totalCount: 0});
      }, []);

      const reset = React.useCallback(
        (resetForm?: boolean) => {
          clearItems();
          offsetRef.current = 0;
          paramsRef.current = resetForm ? ({} as U) : paramsRef.current;
          hasMoreRef.current = false;
        },
        [clearItems],
      );

      React.useEffect(() => {
        if (isFetching || !data) return;

        if (Array.isArray(data)) {
          if (!data.length) {
            if (offsetRef.current === 0) {
              setItemsData({items: []});
            }
            hasMoreRef.current = false;
          } else {
            setItemsData(prevData => ({
              items: offsetRef.current === 0 ? data : [...prevData.items, ...data],
            }));

            hasMoreRef.current = data.length < limitRef.current ? false : true;
          }
        } else if ('data' in data) {
          if (!data.data?.length) {
            if (offsetRef.current === 0) {
              clearItems();
            }
            hasMoreRef.current = false;
          } else {
            setItemsData(prevData => ({
              items: offsetRef.current === 0 ? data?.data : [...prevData.items, ...data?.data],
              totalCount: data?.metadata.totalCount ?? 0,
            }));

            hasMoreRef.current = data.data.length < limitRef.current ? false : true;
          }
        }
      }, [clearItems, data, isFetching]);

      const lazyLoad = React.useCallback(() => {
        if (!hasMoreRef.current || !items.length) return;
        offsetRef.current = offsetRef.current + limitRef.current;
        doSearch({...paramsRef.current, offset: offsetRef.current, limit: limitRef.current});
      }, [doSearch, items.length]);

      const value = React.useMemo(
        () => ({
          items,
          totalCount,
          isLazyLoading: isFetching && offsetRef.current > 0,
          lazyLoad,
          reset,
          isLoading: isFetching && offsetRef.current === 0,
          isError,
          error,
          search,
          searchParams: paramsRef.current,
          clearItems,
        }),
        [clearItems, error, isError, isFetching, items, lazyLoad, reset, search, totalCount],
      );

      return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
    },

    useSearchLazyLoadProvider: () => {
      const context = React.useContext(SearchContext);
      if (!context) {
        throw new Error('useSearchLazyLoadProvider must be used within a SearchLazyLoadProvider');
      }
      return context;
    },
  };
};
